import anime from 'animejs';

import {
  BEBOT_WIDGET_LOADING_CONTAINER,
  BEBOT_WIDGET_LOADING_HEAD,
  BEBOT_WIDGET_LOADING_CIRCLE,
  BEBOT_WIDGET,
  BEBOT_WIDGET_ACTIVE,
  BEBOT_BUBBLE_WRAPPER,
  BEBOT_BUBBLE,
} from './widgetClassNames';
import { MAIN_BUTTON_SIZE_DESKTOP, MAIN_BUTTON_SIZE_MOBILE } from './widget-design';
import { isMobileView } from './responsive';

// nodes related to loading indicator
const getLoadingContainer = () => document.querySelector(`.${BEBOT_WIDGET_LOADING_CONTAINER}`);
const getLoadingHeadImage = () => document.querySelector(`.${BEBOT_WIDGET_LOADING_HEAD}`);
const getLoadingCircle = () => document.querySelectorAll(`.${BEBOT_WIDGET_LOADING_CIRCLE}`);

// nodes related to widget
const getCardRoot = () => document.querySelector(`.${BEBOT_WIDGET}`);
const getCard = () => document.querySelector(`.${BEBOT_WIDGET} > :first-child > :first-child`);
const getBubbleWrapper = () => document.querySelector(`.${BEBOT_BUBBLE_WRAPPER}`);
const getBubble = () => document.querySelector(`.${BEBOT_BUBBLE}`);
const getBubbleImage = () => document.querySelector(`.${BEBOT_BUBBLE} > :first-child`);
const getBubbleText = () => document.querySelector(`.${BEBOT_BUBBLE} > :nth-child(2)`);
const getChat = () => document.querySelectorAll(`.${BEBOT_WIDGET}`)[1];
const getWidgetRoot = () => getCardRoot().parentElement;

const addPx = (originalValue, increaseBy) =>
  `${Number(String(originalValue).replace(/px$/, '')) + increaseBy}px`;

const loadingAnimation = ({ target, renderWidgetLoadingAnimation }) => {
  const widgetLoader = renderWidgetLoadingAnimation();
  target.appendChild(widgetLoader);
  if (isMobileView()) {
    anime({
      targets: getLoadingContainer(),
      translateY: '50%',
      easing: 'easeInOutCubic',
      opacity: 1,
      duration: 500,
      delay: 100,
    });
  }
  anime({
    targets: getLoadingHeadImage(),
    translateY: -20,
    opacity: 1,
    duration: 600,
    delay: 500,
    easing: 'linear',
  });
  anime({
    targets: getLoadingCircle(),
    translateY: 30,
    opacity: 1,
    loop: true,
    duration: 500,
    direction: 'alternate',
    delay: anime.stagger(100, { start: 500 }),
  });
};

const hideBubbleContent = () => {
  anime({
    targets: getBubbleImage(),
    opacity: 0,
    duration: 0,
    display: 'none',
    'pointer-events': 'none',
  });
  // bubbleButton text could be unset so we need to check for the node existing
  if (getBubbleText()) {
    anime({
      targets: getBubbleText(),
      opacity: 0,
      duration: 0,
      display: 'none',
      'pointer-events': 'none',
    });
  }
};

const showBubbleContent = () => {
  anime({
    targets: getBubbleImage(),
    opacity: 1,
    duration: 0,
  });
  // there is a chance the bubble text is unset
  if (getBubbleText()) {
    anime({
      targets: getBubbleText(),
      opacity: 1,
      duration: 0,
    });
  }
};

const hideCardContentAndBubble = ({ instant = false }) => {
  anime({
    targets: getBubbleWrapper(),
    translateY: 50,
    opacity: 0,
    duration: instant ? 0 : 300,
    easing: 'easeInOutCubic',
  });
  anime({
    targets: getCard(),
    opacity: 0,
    duration: instant ? 0 : 300,
  });
};

const showCardContentAndBubble = ({ instant = false }) => {
  anime({
    targets: getCard(),
    opacity: 1,
    duration: instant ? 0 : 500,
    easing: 'easeInOutCubic',
  });
  anime({
    targets: getBubbleWrapper(),
    translateY: 0,
    opacity: 1,
    duration: instant ? 0 : 500,
    easing: 'easeInOutCubic',
  });
};

const morphBubbleIntoChat = ({ chatHasOpened, onMorphComplete, instant = false, align }) => {
  hideBubbleContent();
  const sideMargin = isMobileView() ? '0px' : '10px';
  anime({
    targets: getBubbleWrapper(),
    bottom: sideMargin,
    right: align === 'left' ? 'auto' : sideMargin,
    left: align === 'left' ? sideMargin : 'auto',
    duration: instant ? 0 : 600,
    easing: 'easeInOutCubic',
    'pointer-events': 'none',
  });
  // Morph the bubble into webchat size and shape
  anime({
    targets: getBubble(),
    width: isMobileView() ? window.innerWidth : 350,
    height: isMobileView() ? window.innerHeight : 600,
    backgroundColor: chatHasOpened ? '#fff' : '#7e7d7f',
    borderRadius: isMobileView() ? '0px' : '10px',
    easing: 'easeInOutCubic',
    duration: instant ? 0 : 600,
    complete: onMorphComplete,
  });
};

const morphChatBackIntoBubble = ({ responsiveStyles, instant = false }) => {
  document.body.classList.remove(BEBOT_WIDGET_ACTIVE);
  // Prepare starting state (might be wrong due to loading animation)
  anime({
    targets: getBubbleWrapper(),
    opacity: 1,
    duration: 0,
  });
  anime({
    targets: getBubble(),
    backgroundColor: '#fff',
    duration: 0,
  });
  // Execute the main animation
  anime({
    targets: getBubbleWrapper(),
    width: 'unset',
    height: 'unset',
    bottom: responsiveStyles.bubbleWrapper.bottom,
    right: responsiveStyles.bubbleWrapper.right,
    left: responsiveStyles.bubbleWrapper.left,
    easing: 'easeInOutCubic',
    duration: instant ? 0 : 500,
  });
  anime({
    targets: getBubble(),
    width: isMobileView() ? MAIN_BUTTON_SIZE_MOBILE : MAIN_BUTTON_SIZE_DESKTOP,
    height: isMobileView() ? MAIN_BUTTON_SIZE_MOBILE : MAIN_BUTTON_SIZE_DESKTOP,
    bottom: 0,
    borderRadius: '50px',
    backgroundColor: responsiveStyles.bubbleButton['background-color'],
    easing: 'easeInOutCubic',
    duration: instant ? 0 : 500,
  });
  showBubbleContent();
};

const morphCardIntoChat = ({ chatHasOpened, onMorphComplete, instant = false, align }) => {
  hideCardContentAndBubble({ instant });
  // Morph the card into webchat size
  anime({
    targets: getCardRoot(),
    translateY: 90,
    bottom: 100,
    right: align === 'left' ? 'auto' : 10,
    left: align === 'left' ? 10 : 'auto',
    width: 350,
    height: 600,
    backgroundColor: chatHasOpened ? '#fff' : '#7e7d7f',
    easing: 'easeInOutCubic',
    duration: instant ? 0 : 350,
    delay: 50,
    'z-Index': 1,
    complete: onMorphComplete,
  });
};

const morphChatBackIntoCard = ({ responsiveStyles, instant = false }) => {
  // Prepare starting state (might be wrong due to loading animation)
  anime({
    targets: getCardRoot(),
    backgroundColor: '#fff',
    opacity: 1,
    duration: 0,
  });
  // Execute the main animation
  anime({
    targets: getCardRoot(),
    width: 250,
    height: 121,
    bottom: addPx(responsiveStyles.cardView.bottom, 90),
    left: responsiveStyles.cardView.left,
    right: responsiveStyles.cardView.right,
    easing: 'easeInOutCubic',
    duration: instant ? 0 : 500,
  });
  showCardContentAndBubble({ instant });
};

/**
 * Morphs the widget card or circle button into chat window (either full screen or not)
 */
export const openAnimation = ({
  chatHasOpened,
  isCardHidden,
  renderWidgetLoadingAnimation,
  prepareChat,
  createHideClass,
  setChatStyle,
  onLoad,
  align,
}) => {
  const morphBubbleInsteadOfCard = isCardHidden || isMobileView();

  const onMorphComplete = () => {
    if (isMobileView()) {
      if (!chatHasOpened) createHideClass();
      document.body.classList.add(BEBOT_WIDGET_ACTIVE);
    }
    if (chatHasOpened) {
      getChat().style.setProperty('display', 'block');
      getWidgetRoot().style.setProperty('display', 'none');
      setChatStyle();
    } else {
      const afterLoadAnimation = () => {
        // fade out the container that was morphed into chat shape (with loading animation inside)
        anime({
          targets: morphBubbleInsteadOfCard ? getBubbleWrapper() : getCardRoot(),
          opacity: 0,
          duration: 600,
          delay: 1000,
          easing: 'easeInOutCubic',
          complete: () => {
            getWidgetRoot().style.setProperty('display', 'none');
            const loadingContainer = getLoadingContainer();
            if (loadingContainer) loadingContainer.remove();
            onLoad();
          },
        });
      };
      prepareChat(afterLoadAnimation);
    }
  };

  if (!chatHasOpened) {
    loadingAnimation({
      target: morphBubbleInsteadOfCard ? getBubble() : getCardRoot(),
      renderWidgetLoadingAnimation,
    });
  }

  if (morphBubbleInsteadOfCard) {
    morphBubbleIntoChat({ chatHasOpened, onMorphComplete, align });
  } else {
    morphCardIntoChat({ chatHasOpened, onMorphComplete, align });
  }
};

/**
 * Closes/hides the chat by morphing it back into the widget card/bubble
 */
export const closeAnimation = ({ isCardHidden, responsiveStyles }) => {
  if (isCardHidden || isMobileView()) {
    morphChatBackIntoBubble({ responsiveStyles });
  } else {
    morphChatBackIntoCard({ responsiveStyles });
  }
};

/**
 * Adjusts animated elements of an open chat, so that closing the chat will work well.
 *
 * E.g. if the user has opened the chat while in desktop mode (and the card has morphed into the
 * chat window shape), but then resized the browser window to mobile size and closed the chat,
 * the chat window should morph into the bubble window.
 */
export const adjustAnimationAfterResize = ({ isCardHidden, responsiveStyles, align }) => {
  // Assumption: the chat is open, we just need to change properties of some invisible elements
  if (isCardHidden || isMobileView()) {
    // will morph back into the bubble, so prepare the stage for that
    morphChatBackIntoCard({ responsiveStyles, instant: true });
    morphBubbleIntoChat({ chatHasOpened: true, onMorphComplete: () => {}, instant: true, align });
  } else {
    // will morph back into the card, so prepare the stage for that
    morphChatBackIntoBubble({ responsiveStyles, instant: true });
    morphCardIntoChat({ chatHasOpened: true, onMorphComplete: () => {}, instant: true, align });
  }
};
